/**
 * Model name → symbol.
 *
 * The registry stores only a free-text name (there is no model_type column), so the card's
 * symbol is inferred from that name: "PPE Compliance — Mining" gets the compliance shield,
 * "ANPR Engine" gets the plate reader, and anything unrecognised keeps the generic chip.
 * Purely presentational — nothing here is persisted or sent to the backend.
 *
 * Matching is first-hit against `match`, so entries are ordered most-specific first:
 * "license plate" must beat the plain "vehicle" rule.
 */
import type { ComponentType, SVGProps } from 'react';
import {
  CpuChipIcon,
  FaceSmileIcon,
  UserIcon,
  UserGroupIcon,
  ShieldExclamationIcon,
  ShieldCheckIcon,
  TruckIcon,
  ViewfinderCircleIcon,
  ExclamationTriangleIcon,
  ArrowTrendingDownIcon,
  ChartBarIcon,
  TableCellsIcon,
  FireIcon
} from '@heroicons/react/24/outline';

type IconType = ComponentType<SVGProps<SVGSVGElement>>;

export interface ModelSymbol {
  Icon: IconType;
  /** Tailwind palette classes for the icon tile — light + dark. */
  tile: string;
  /** Tailwind palette classes for the glyph — light + dark. */
  glyph: string;
  /** Human label for the inferred kind, used as the tile's tooltip. */
  label: string;
}

interface Rule extends ModelSymbol {
  /** Lower-cased substrings; any hit claims the model. */
  match: string[];
  /**
   * Substrings that disqualify a name that otherwise matched — the same escape
   * hatch modelSurfaces.ts uses, and needed for the same reason: short match
   * strings live inside longer unrelated words ("fire" inside "firearm").
   */
  exclude?: string[];
}

const RULES: Rule[] = [
  {
    match: ['license plate', 'licence plate', 'number plate', 'anpr', 'lpr'],
    Icon: ViewfinderCircleIcon,
    tile: 'bg-violet-50 dark:bg-violet-900/20',
    glyph: 'text-violet-600 dark:text-violet-400',
    label: 'Licence plate (ANPR)',
  },
  {
    match: ['ppe', 'helmet', 'safety vest', 'compliance'],
    Icon: ShieldCheckIcon,
    tile: 'bg-orange-50 dark:bg-orange-900/20',
    glyph: 'text-orange-600 dark:text-orange-400',
    label: 'PPE compliance',
  },
  {
    match: ['intrusion', 'trespass', 'perimeter'],
    Icon: ShieldExclamationIcon,
    tile: 'bg-red-50 dark:bg-red-900/20',
    glyph: 'text-red-600 dark:text-red-400',
    label: 'Intrusion detection',
  },
  {
    // One detector, both classes — a model named "Smoke Detector v2" is the same
    // thing as one named "Fire Detection" and gets the same symbol.
    match: ['fire', 'smoke', 'flame'],
    // "firearm" starts with "fire" and is a weapon detector, not a fire detector.
    exclude: ['firearm'],
    Icon: FireIcon,
    tile: 'bg-amber-50 dark:bg-amber-900/20',
    glyph: 'text-amber-600 dark:text-amber-400',
    label: 'Fire & smoke detection',
  },
  {
    match: ['theft', 'shoplift', 'pilfer'],
    Icon: ExclamationTriangleIcon,
    tile: 'bg-amber-50 dark:bg-amber-900/20',
    glyph: 'text-amber-600 dark:text-amber-400',
    label: 'Theft detection',
  },
  {
    match: ['fall'],
    Icon: ArrowTrendingDownIcon,
    tile: 'bg-rose-50 dark:bg-rose-900/20',
    glyph: 'text-rose-600 dark:text-rose-400',
    label: 'Fall detection',
  },
  {
    // A density grid reads as a heatmap; a flame reads as fire detection.
    match: ['heatmap', 'heat map'],
    Icon: TableCellsIcon,
    tile: 'bg-fuchsia-50 dark:bg-fuchsia-900/20',
    glyph: 'text-fuchsia-600 dark:text-fuchsia-400',
    label: 'Heatmap',
  },
  {
    match: ['face', 'facial'],
    Icon: FaceSmileIcon,
    tile: 'bg-pink-50 dark:bg-pink-900/20',
    glyph: 'text-pink-600 dark:text-pink-400',
    label: 'Face detection',
  },
  {
    // Counting traffic is a different job from spotting a vehicle, and the design gives
    // it its own symbol — so it has to be claimed before the general vehicle rule.
    match: ['vehicle count', 'traffic count'],
    Icon: ChartBarIcon,
    tile: 'bg-teal-50 dark:bg-teal-900/20',
    glyph: 'text-teal-600 dark:text-teal-400',
    label: 'Vehicle counting',
  },
  {
    match: ['vehicle', 'truck', 'car ', 'traffic'],
    Icon: TruckIcon,
    tile: 'bg-blue-50 dark:bg-blue-900/20',
    glyph: 'text-blue-600 dark:text-blue-400',
    label: 'Vehicle detection',
  },
  {
    // "person count" / "occupancy" / "crowd" — a headcount rather than a single person.
    match: ['count', 'occupancy', 'crowd', 'footfall'],
    Icon: UserGroupIcon,
    tile: 'bg-emerald-50 dark:bg-emerald-900/20',
    glyph: 'text-emerald-600 dark:text-emerald-400',
    label: 'People counting',
  },
  {
    match: ['person', 'people', 'human', 'in/out', 'in out'],
    Icon: UserIcon,
    tile: 'bg-sky-50 dark:bg-sky-900/20',
    glyph: 'text-sky-600 dark:text-sky-400',
    label: 'Person detection',
  },
];

/** Fallback for a name that matches no rule — the generic model chip. */
const GENERIC: ModelSymbol = {
  Icon: CpuChipIcon,
  tile: 'bg-purple-50 dark:bg-purple-900/20',
  glyph: 'text-purple-600 dark:text-purple-400',
  label: 'Analytics model',
};

export function modelSymbol(name?: string | null): ModelSymbol {
  const n = (name || '').toLowerCase();
  if (!n) return GENERIC;
  const hit = RULES.find(
    (r) => !(r.exclude || []).some((x) => n.includes(x)) && r.match.some((m) => n.includes(m))
  );
  return hit ? { Icon: hit.Icon, tile: hit.tile, glyph: hit.glyph, label: hit.label } : GENERIC;
}
